import {
    DateRangeCalendar,
    ReactSelect,
    InfoBanner,
    TableHeader,
    FiltersButton,
    WithRoleAccess,
} from "@services/ui-components";
import { Row, Col } from "react-bootstrap";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import SalaryTable from "./SalaryTable";
import moment from "moment";
import Button from "react-bootstrap/Button";
import { ApolloError, useReactiveVar } from "@apollo/client";
import ErrorService from "../../services/ErrorService";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { useGetAllEmployeesQuery } from "@me-team/host/main/salary/graphql/salary.hooks";
import { CompanyAccess, PermissionScope } from "@me-team/host/main/graphql/types";
import { currentBranchEmployeeIdVar, currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { DatesRange } from "../../interfaces/interfaces";
import { EPOCH_DATE } from "@services/ui-components/src/DateRangeCalendar/constants";
import { getEmployeeOptions } from "./constans";
import { AccessContext } from "@me-team/host/src/components/App/App";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import AccessService from "@me-team/host/services/AccessService";
import Spinner from "@services/ui-components/src/Spinner/Spinner";

interface Option {
    value: number;
    label: string;
}

export interface Filter {
    dateRangeFilter: DatesRange;
    employeeFilter?: number;
}

const Salary: React.FC = () => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const navigate = useNavigate();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const { isReady: isAccessContextReady } = useContext(AccessContext);
    const accessService = AccessService.getInstance();
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const canViewSalaryAll = useRoleAccess(CompanyAccess.SalaryView, { scope: PermissionScope.All });
    const canViewSalarySelf = useRoleAccess(CompanyAccess.SalaryView, { scope: PermissionScope.Self });
    const canViewSalaryPage = canViewSalaryAll || canViewSalarySelf;
    const salaryViewScope = canViewSalaryAll ? PermissionScope.All : PermissionScope.Self;
    const shouldShowOnlyOwnSalaryRows = canViewSalarySelf && !canViewSalaryAll;
    const canViewPaymentsHistory = useRoleAccess(CompanyAccess.PaymentsHistoryView);
    const canCreatePayment = useRoleAccess(CompanyAccess.PaymentCreate);
    const canOpenEmployeeAll = useRoleAccess(CompanyAccess.EmployeeView, {
        scope: PermissionScope.All,
    });
    const canOpenEmployeeSelf = useRoleAccess(CompanyAccess.EmployeeView, {
        scope: PermissionScope.Self,
    });
    const canOpenService = useRoleAccess(CompanyAccess.ServiceListView);
    const canFilterByEmployee = canViewSalaryAll || canViewSalarySelf;
    const isSalaryAccessPending =
        !isAccessContextReady || !accessService.isRoleAccessReady() || accessService.isRoleAccessLoading();
    const salaryPageAccessMode = canViewSalaryPage ? "view" : "forbidden";
    const salaryAccessContract = {
        isSalaryAccessPending,
        canViewSalaryAll,
        canViewSalarySelf,
        canViewSalaryPage,
        salaryViewScope,
        shouldShowOnlyOwnSalaryRows,
        salaryPageAccessMode,
        canViewPaymentsHistory,
        canCreatePayment,
        canOpenEmployeeAll,
        canOpenEmployeeSelf,
        canOpenService,
        canFilterByEmployee,
    };

    const initialFiltersRange = {
        startDate: moment(new Date(null)).format("YYYY-MM-DD"),
        endDate: moment(new Date(null)).format("YYYY-MM-DD"),
    };
    const [dateRangeFilter, setDateRangeFilter] = useState(initialFiltersRange);

    const [showFilters, setShowFilters] = useState(false);
    const [selectedEmployees, setSelectedEmployees] = useState(null);

    const { data: employeesData } = useGetAllEmployeesQuery({
        variables: { companyId: +currentCompanyId, branchId: Number(branchId) },
        skip:
            !currentCompanyId ||
            salaryAccessContract.isSalaryAccessPending ||
            salaryAccessContract.salaryPageAccessMode === "forbidden",
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const allEmployeeOptions: Option[] = getEmployeeOptions(employeesData?.user?.company?.employees?.employees);
    const employeeOptions: Option[] = salaryAccessContract.shouldShowOnlyOwnSalaryRows
        ? allEmployeeOptions.filter((employee) => employee.value === currentBranchEmployeeId)
        : allEmployeeOptions;
    const selfOption = salaryAccessContract.shouldShowOnlyOwnSalaryRows
        ? (employeeOptions.find((employee) => employee.value === currentBranchEmployeeId) ?? null)
        : null;

    const initDateFilter: DatesRange = {
        startDate: null,
        endDate: null,
    };

    const [filter, setFilter] = useState<Filter>({
        dateRangeFilter: initDateFilter,
        employeeFilter: null,
    });
    const toggleFilters = () => setShowFilters(!showFilters);

    useEffect(() => {
        if (!salaryAccessContract.shouldShowOnlyOwnSalaryRows || !selfOption) return;

        if (selectedEmployees?.value !== selfOption.value) {
            setSelectedEmployees(selfOption);
        }

        setFilter((prev) =>
            prev.employeeFilter === selfOption.value ? prev : { ...prev, employeeFilter: selfOption.value }
        );
    }, [salaryAccessContract.shouldShowOnlyOwnSalaryRows, selfOption, selectedEmployees?.value]);

    const handleEmployeesChange = (selectedOption: Option) => {
        setSelectedEmployees(selectedOption);
    };

    const handleApplyFilter = () => {
        const appliedEmployee = salaryAccessContract.shouldShowOnlyOwnSalaryRows ? selfOption : selectedEmployees;

        setFilter({
            dateRangeFilter: {
                startDate: dateRangeFilter.startDate !== EPOCH_DATE ? dateRangeFilter.startDate : null,
                endDate: dateRangeFilter.endDate !== EPOCH_DATE ? dateRangeFilter.endDate : null,
            },
            employeeFilter: appliedEmployee?.value ? appliedEmployee.value : null,
        });
    };

    const handleClearFilter = () => {
        setFilter({
            dateRangeFilter: initDateFilter,
            employeeFilter: selfOption?.value ?? null,
        });
        setSelectedEmployees(selfOption);
        setDateRangeFilter(initialFiltersRange);
    };

    if (!currentCompanyId || salaryAccessContract.isSalaryAccessPending) {
        return <Spinner />;
    }

    return (
        <div className="mb-9">
            <InfoBanner>
                <p className="fs-7 mb-0 text-violet-hover">{t("A section for managing employees'" + " salaries")}</p>
            </InfoBanner>

            <TableHeader
                title={t("Salary")}
                paths={[
                    { path: `/employees-list/${branchId}`, label: t("Personnel Management") },
                    { path: `/personnel/${currentCompanyId}/salary/${branchId}`, label: t("Salary") },
                ]}
                colSize={4}
                btnColSize={8}
                buttons={
                    <Row className="gx-3 justify-content-end align-items-center mt-4 mt-lg-0">
                        <WithRoleAccess permission={CompanyAccess.PaymentsHistoryView}>
                            <Col xs={6} lg={4} xxl={3}>
                                <Button
                                    variant="primary"
                                    className="w-100"
                                    onClick={() =>
                                        navigate(`/personnel/${currentCompanyId}/payment-history/${branchId}`)
                                    }
                                >
                                    {t("History of Payments")}
                                </Button>
                            </Col>
                        </WithRoleAccess>
                        <Col xs={6} lg={4} xl={3} xxl={2}>
                            <FiltersButton filter={filter} showFilters={showFilters} onClick={toggleFilters} />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
            >
                <Row className="gx-3 pt-1 mb-4 pb-2 gap-3 gap-lg-0">
                    <Col lg={8} xl={9}>
                        <Row className="gx-3">
                            <Col xs={12} sm={6} className="text-primary mb-3 mb-sm-0">
                                <DateRangeCalendar
                                    dateRangeFilter={dateRangeFilter}
                                    setDateRangeFilter={setDateRangeFilter}
                                    id="salary-rangeDate-field"
                                    disableValidation={true}
                                />
                            </Col>
                            {salaryAccessContract.canFilterByEmployee ? (
                                <Col xs={12} sm={6} className="text-primary">
                                    <ReactSelect
                                        id="salary-employees-field"
                                        name="employees"
                                        value={selectedEmployees}
                                        options={employeeOptions}
                                        placeholder={t("employee")}
                                        onChange={handleEmployeesChange}
                                        isSearchable={true}
                                    />
                                </Col>
                            ) : null}
                        </Row>
                    </Col>
                    <Col lg={4} xl={3}>
                        <Row className="gx-3 h-100">
                            <Col xs={6}>
                                <Button
                                    variant="outline-dark"
                                    type="button"
                                    className="border-grey w-100 h-100 fw-normal"
                                    onClick={handleClearFilter}
                                >
                                    {t("clear")}
                                </Button>
                            </Col>
                            <Col xs={6}>
                                <Button variant="primary" className="w-100 h-100" onClick={handleApplyFilter}>
                                    {t("apply")}
                                </Button>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </TableHeader>

            <SalaryTable
                filter={filter}
                shouldShowOnlyOwnSalaryRows={salaryAccessContract.shouldShowOnlyOwnSalaryRows}
                canCreatePayment={salaryAccessContract.canCreatePayment}
                canOpenEmployeeAll={salaryAccessContract.canOpenEmployeeAll}
                canOpenEmployeeSelf={salaryAccessContract.canOpenEmployeeSelf}
                canOpenService={salaryAccessContract.canOpenService}
            />
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </div>
    );
};

export default Salary;
